// app/src/lib/crypto.js — encryption primitives for backup blobs, WAL entries,
// and any other at-rest payload.
//
// Threat model:
//   - Attacker has read access to S3 bucket contents.
//   - Attacker does NOT have access to the user's Cognito ID token or the per-device
//     salt stored in IndexedDB.
//   - Loss of either secret = data is unrecoverable. Loss alone of S3 = no plaintext leak.
//
// Construction:
//   - Master keying material = HKDF(secret = Cognito sub, salt = per-device random 32B,
//                                   info = "kanjo/v1/backup", hash = SHA-256).
//   - Per-purpose subkeys derived with distinct `info` strings to avoid cross-purpose
//     key reuse.
//   - AEAD: AES-256-GCM, random 12-byte IV per encryption (NEVER reused with same key).
//   - Envelope: 1-byte version || 12-byte IV || ciphertext-with-tag.
//   - No fallback to legacy formats — versioning is forward-only.
//
// All operations use SubtleCrypto via non-extractable CryptoKey handles so raw key
// material never appears in JS heap that we can leak via dev-tools or accidental logs.

/** @typedef {"backup" | "wal"} Purpose */

const VERSION = 0x01;
const IV_BYTES = 12;
const SALT_KEY = 'kanjo.crypto.salt.v1';

const enc = new TextEncoder();
const dec = new TextDecoder();

/**
 * @typedef {Object} DerivedKeys
 * @property {CryptoKey} backupKey - AES-256-GCM key for full snapshot backup blobs.
 * @property {CryptoKey} walKey    - AES-256-GCM key for transaction WAL entries.
 * @property {CryptoKey} piiKey    - AES-256-GCM key for in-Dexie PII fields (e.g. 1099 TIN).
 */

/** Get-or-create a per-device 32-byte random salt. Persisted in IndexedDB, NOT localStorage. */
async function getOrCreateSalt(idb) {
  const existing = await idb.get(SALT_KEY);
  if (existing instanceof Uint8Array && existing.length === 32) return existing;
  const fresh = crypto.getRandomValues(new Uint8Array(32));
  await idb.set(SALT_KEY, fresh);
  return fresh;
}

/**
 * Derive per-purpose AES-256-GCM CryptoKeys from a Cognito `sub` claim.
 *
 * @param {string} cognitoSub - opaque user identifier from the validated ID token.
 * @param {{ get: (k: string) => Promise<any>, set: (k: string, v: any) => Promise<void> }} idb
 *        Tiny IndexedDB shim used to store the per-device salt.
 * @returns {Promise<DerivedKeys>}
 */
export async function deriveKeys(cognitoSub, idb) {
  if (typeof cognitoSub !== 'string' || cognitoSub.length < 8) {
    throw new Error('deriveKeys: invalid cognitoSub');
  }
  const salt = await getOrCreateSalt(idb);
  const ikm = await crypto.subtle.importKey(
    'raw',
    enc.encode(cognitoSub),
    'HKDF',
    false,
    ['deriveKey'],
  );
  const make = (info) => crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: enc.encode(info) },
    ikm,
    { name: 'AES-GCM', length: 256 },
    false, // non-extractable
    ['encrypt', 'decrypt'],
  );
  const [backupKey, walKey, piiKey] = await Promise.all([
    make('kanjo/v1/backup'),
    make('kanjo/v1/wal'),
    make('kanjo/v1/pii'),
  ]);
  return { backupKey, walKey, piiKey };
}

/**
 * Encrypt `plaintext` with AES-256-GCM.
 *
 * @param {CryptoKey} key
 * @param {Uint8Array} plaintext
 * @param {Uint8Array} [aad] - optional additional authenticated data (e.g. userId binding).
 * @returns {Promise<Uint8Array>} versioned envelope.
 */
export async function encrypt(key, plaintext, aad) {
  if (!(plaintext instanceof Uint8Array)) throw new Error('encrypt: plaintext must be Uint8Array');
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const params = { name: 'AES-GCM', iv, tagLength: 128 };
  if (aad) params.additionalData = aad;
  const ct = new Uint8Array(await crypto.subtle.encrypt(params, key, plaintext));
  const out = new Uint8Array(1 + IV_BYTES + ct.length);
  out[0] = VERSION;
  out.set(iv, 1);
  out.set(ct, 1 + IV_BYTES);
  return out;
}

/**
 * Decrypt a versioned envelope. Throws on tampering, wrong key, or unknown version.
 *
 * @param {CryptoKey} key
 * @param {Uint8Array} envelope
 * @param {Uint8Array} [aad]
 * @returns {Promise<Uint8Array>}
 */
export async function decrypt(key, envelope, aad) {
  if (!(envelope instanceof Uint8Array)) throw new Error('decrypt: envelope must be Uint8Array');
  if (envelope.length < 1 + IV_BYTES + 16) throw new Error('decrypt: envelope too short');
  if (envelope[0] !== VERSION) throw new Error(`decrypt: unsupported version 0x${envelope[0].toString(16)}`);
  const iv = envelope.subarray(1, 1 + IV_BYTES);
  const ct = envelope.subarray(1 + IV_BYTES);
  const params = { name: 'AES-GCM', iv, tagLength: 128 };
  if (aad) params.additionalData = aad;
  return new Uint8Array(await crypto.subtle.decrypt(params, key, ct));
}

/** Convenience: encrypt a JS object as JSON. */
export async function encryptJson(key, obj, aad) {
  return encrypt(key, enc.encode(JSON.stringify(obj)), aad);
}

/** Convenience: decrypt a JSON envelope into a JS object. */
export async function decryptJson(key, envelope, aad) {
  return JSON.parse(dec.decode(await decrypt(key, envelope, aad)));
}

/** Constant-time byte-array equality. Use for HMAC tags, signatures, etc. */
export function timingSafeEqual(a, b) {
  if (!(a instanceof Uint8Array) || !(b instanceof Uint8Array)) return false;
  if (a.length !== b.length) return false;
  let acc = 0;
  for (let i = 0; i < a.length; i++) acc |= a[i] ^ b[i];
  return acc === 0;
}

/** Base64url decode (no padding, URL-safe). */
export function b64uDecode(s) {
  s = s.replace(/-/g, '+').replace(/_/g, '/');
  while (s.length % 4) s += '=';
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Base64url encode (no padding). */
export function b64uEncode(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** SHA-256 → hex. */
export async function sha256Hex(input) {
  const buf = typeof input === 'string' ? enc.encode(input) : input;
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', buf));
  return Array.from(hash, (b) => b.toString(16).padStart(2, '0')).join('');
}
